import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Phone,
  Mail,
  User,
  IndianRupee,
  Users,
  Building2,
  FileText,
  Printer,
  Edit3,
  Receipt,
  FileDown,
  Trash2,
  CheckCircle2,
  Sparkles
} from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import { generateInvoicePdf } from "@/utils/generateInvoicePdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  confirmed: "bg-emerald-100 text-emerald-900 border-emerald-300",
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  completed: "bg-blue-100 text-blue-900 border-blue-300",
  cancelled: "bg-rose-100 text-rose-900 border-rose-300"
};

export default function BookingDetailPage({ businessInfo }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const fetchBooking = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/bookings/${id}`);
      setBooking(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  const handleUpdateStatus = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      const res = await axios.put(`${API}/bookings/${id}`, { status: newStatus });
      setBooking(res.data);
    } catch (err) {
      alert("Failed to update status.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to permanently delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`);
      navigate("/bookings");
    } catch (err) {
      alert("Failed to delete booking.");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-pink-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="bg-white p-12 rounded-3xl border border-pink-100 text-center space-y-4">
        <h2 className="text-xl font-bold text-pink-950">Booking Not Found</h2>
        <Link
          to="/bookings"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-pink-900 text-white text-xs font-bold"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Bookings</span>
        </Link>
      </div>
    );
  }

  const clientName = booking.clientName || booking.customer_name || booking.client_name || "—";
  const phoneNum = booking.phone || booking.client_phone || booking.customer_phone || "—";
  const emailVal = booking.email || booking.client_email || booking.customer_email || "";
  const eventType = booking.event_type || booking.eventType || "Event";
  const statusKey = (booking.booking_status || booking.status || "confirmed").toLowerCase();

  const startDate = booking.eventDate || booking.start_date || booking.date || (booking.bookedDates && booking.bookedDates[0]) || (booking.booked_dates && booking.booked_dates[0]) || "—";
  const endDate = booking.endDate || booking.end_date || startDate;
  const startTime = booking.start_time || "06:00";
  const endTime = booking.end_time || "23:59";
  const slotType = booking.slot_type || "Day";
  const venueSection = booking.hall_section || "Main Grand Hall";
  const guestCount = booking.guest_count ?? booking.guestCount ?? "—";

  const rawTotal = booking.totalAmount ?? booking.total_rent ?? booking.total_amount ?? booking.rent_amount;
  const rawAdvance = booking.advancePaid ?? booking.deposit_amount ?? booking.advance_paid ?? booking.advance_amount;
  const displayTotal = rawTotal !== undefined && rawTotal !== null && !isNaN(Number(rawTotal)) ? Number(rawTotal) : null;
  const displayAdvance = rawAdvance !== undefined && rawAdvance !== null && !isNaN(Number(rawAdvance)) ? Number(rawAdvance) : null;
  const displayBalance = (displayTotal !== null && displayAdvance !== null) ? Math.max(0, displayTotal - displayAdvance) : (booking.balanceDue ?? booking.balance_due ?? null);

  const rawHallRent = booking.hall_rent ?? booking.hallRent ?? booking.rent_amount;
  const displayHallRent = rawHallRent !== undefined && rawHallRent !== null && !isNaN(Number(rawHallRent)) ? Number(rawHallRent) : (displayTotal ?? null);
  const rawLabor = booking.labor_charges ?? booking.laborCharges;
  const displayLabor = rawLabor !== undefined && rawLabor !== null && !isNaN(Number(rawLabor)) ? Number(rawLabor) : null;
  const rawElectricity = booking.electricity_charges ?? booking.electricityCharges;
  const displayElectricity = rawElectricity !== undefined && rawElectricity !== null && !isNaN(Number(rawElectricity)) ? Number(rawElectricity) : null;
  const rawMaint = booking.maintenance_fee ?? booking.maintenanceFee;
  const displayMaintenance = rawMaint !== undefined && rawMaint !== null && !isNaN(Number(rawMaint)) ? Number(rawMaint) : null;
  const hasGst = Boolean(booking.apply_gst || booking.applyGst || booking.sgst_amount > 0 || booking.cgst_amount > 0);
  const displaySgst = Number(booking.sgst_amount ?? booking.sgstAmount ?? (hasGst && displayHallRent ? displayHallRent * 0.09 : 0));
  const displayCgst = Number(booking.cgst_amount ?? booking.cgstAmount ?? (hasGst && displayHallRent ? displayHallRent * 0.09 : 0));

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {/* Top Breadcrumbs / Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to="/bookings"
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-pink-900 hover:text-pink-950 bg-white px-4 py-2 rounded-xl border border-pink-200 shadow-sm transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Bookings</span>
        </Link>

        {/* Quick Actions Header */}
        <div className="flex items-center gap-2">
          <Link
            to={`/bookings/${id}/edit`}
            data-testid="btn-edit-booking-times"
            className="px-4 py-2 rounded-xl bg-pink-900 hover:bg-pink-950 text-white text-xs font-bold shadow flex items-center gap-1.5 transition-colors"
          >
            <Edit3 className="w-3.5 h-3.5" />
            <span>Edit Booking & Times</span>
          </Link>

          <button
            data-testid="btn-download-modal-invoice"
            onClick={() => generateInvoicePdf(booking, businessInfo)}
            className="px-4 py-2 rounded-xl bg-white hover:bg-pink-50 text-pink-900 border border-pink-200 text-xs font-bold shadow-sm flex items-center gap-1.5 transition-colors"
          >
            <FileDown className="w-3.5 h-3.5" />
            <span>Download Invoice</span>
          </button>
        </div>
      </div>

      {/* Main Full-Screen Details Card */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-xl overflow-hidden">
        {/* Banner */}
        <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 p-6 sm:p-8 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-xs font-bold mb-1">
              <span>{eventType} Booking</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black">{clientName}</h1>
            <p className="text-pink-100 text-xs font-mono">Booking ID: {booking.id}</p>
          </div>

          <div className="flex items-center gap-3">
            <span
              className={`px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-wider border shadow-sm ${
                STATUS_COLORS[statusKey] || STATUS_COLORS.confirmed
              }`}
            >
              {statusKey}
            </span>
          </div>
        </div>

        {/* Body Content */}
        <div className="p-6 sm:p-8 space-y-6 bg-gray-50/30">
          
          {/* Schedule & Timing Grid */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-pink-100 pb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-pink-700" />
                <span>Event Schedule & Duration</span>
              </h3>
              <Link
                to={`/bookings/${id}/edit`}
                className="text-xs font-bold text-pink-700 hover:text-pink-950 underline flex items-center gap-1"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Edit Times</span>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-2xl bg-pink-50/60 border border-pink-100">
                <span className="text-xs text-gray-500 font-semibold block">Date(s)</span>
                <span className="font-bold text-pink-950 text-base">
                  {startDate === endDate || !endDate
                    ? startDate
                    : `${startDate} → ${endDate}`}
                </span>
                <span className="text-[11px] text-gray-500 block mt-1">
                  {booking.booking_type === "multi_day" ? "Multi-Day Booking" : "Single-Day Function"}
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-pink-50/60 border border-pink-100">
                <span className="text-xs text-gray-500 font-semibold block">Function Hours (24h)</span>
                <span className="font-bold text-pink-950 text-base font-mono">
                  {startTime} - {endTime}
                </span>
                <span className="text-[11px] text-pink-700 font-bold block mt-1 uppercase">
                  {slotType} SLOT
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-pink-50/60 border border-pink-100">
                <span className="text-xs text-gray-500 font-semibold block">Venue Section</span>
                <span className="font-bold text-pink-950 text-base">
                  {venueSection}
                </span>
                <span className="text-[11px] text-gray-500 block mt-1">
                  Expected: {guestCount !== "—" ? `${guestCount} Guests` : "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Customer & Contact Info */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2 border-b border-pink-100 pb-3">
              <User className="w-4 h-4 text-pink-700" />
              <span>Customer & Contact Information</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-pink-700" />
                <span className="text-gray-500 font-medium">Phone:</span>
                <span className="font-bold text-gray-900">{phoneNum}</span>
              </div>

              {emailVal && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-pink-700" />
                  <span className="text-gray-500 font-medium">Email:</span>
                  <span className="font-bold text-gray-900">{emailVal}</span>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-pink-700" />
                <span className="text-gray-500 font-medium">Guest Capacity:</span>
                <span className="font-bold text-gray-900">{guestCount !== "—" ? `${guestCount} Attendees` : "—"}</span>
              </div>

              <div className="flex items-center gap-3">
                <Building2 className="w-4 h-4 text-pink-700" />
                <span className="text-gray-500 font-medium">Venue:</span>
                <span className="font-bold text-gray-900">{venueSection}</span>
              </div>
            </div>
          </div>

          {/* Complete Financial Details Breakdown */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-pink-100 pb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-pink-700" />
                <span>Financial Details & Charges (Mobile App Synchronized)</span>
              </h3>
              <Link
                to={`/bookings/${id}/financials`}
                className="text-xs font-bold text-pink-700 hover:text-pink-950 underline"
              >
                View Full Breakdown →
              </Link>
            </div>

            {/* Itemized Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                <span className="text-gray-500 text-[11px] block">Hall Rent</span>
                <span className="font-bold text-gray-900 font-mono text-sm">
                  {displayHallRent !== null ? `₹${Number(displayHallRent).toLocaleString("en-IN")}` : "—"}
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                <span className="text-gray-500 text-[11px] block">Labor Charges</span>
                <span className="font-bold text-gray-900 font-mono text-sm">
                  {displayLabor !== null ? `₹${Number(displayLabor).toLocaleString("en-IN")}` : "—"}
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                <span className="text-gray-500 text-[11px] block">Electricity</span>
                <span className="font-bold text-gray-900 font-mono text-sm">
                  {displayElectricity !== null ? `₹${Number(displayElectricity).toLocaleString("en-IN")}` : "—"}
                </span>
              </div>

              <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                <span className="text-gray-500 text-[11px] block">Maintenance</span>
                <span className="font-bold text-gray-900 font-mono text-sm">
                  {displayMaintenance !== null ? `₹${Number(displayMaintenance).toLocaleString("en-IN")}` : "—"}
                </span>
              </div>
            </div>

            {/* 18% GST */}
            {hasGst && (
              <div className="p-3.5 rounded-2xl bg-pink-50/70 border border-pink-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">SGST (9% on Hall Rent):</span>
                  <span className="font-bold text-pink-950 font-mono">₹{Number(displaySgst).toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">CGST (9% on Hall Rent):</span>
                  <span className="font-bold text-pink-950 font-mono">₹{Number(displayCgst).toLocaleString("en-IN")}</span>
                </div>
              </div>
            )}

            {/* Additional Charges */}
            {booking.additional_charges && booking.additional_charges.length > 0 && (
              <div className="space-y-2 pt-1">
                <span className="text-xs font-bold text-gray-700 block">Additional Venue Services:</span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                  {booking.additional_charges.map((ac, i) => (
                    <div key={i} className="p-2.5 rounded-xl bg-gray-50 border border-pink-100 flex justify-between">
                      <span className="text-gray-700 font-medium">{ac.name || `Fee #${i + 1}`}</span>
                      <span className="font-bold text-pink-950 font-mono">₹{Number(ac.amount || 0).toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Master Totals Banner */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center pt-3 border-t border-pink-100">
              <div className="p-4 rounded-2xl bg-pink-100/70 border border-pink-300">
                <span className="text-xs text-gray-600 font-bold block">Total Booking Amount</span>
                <span className="text-xl font-black text-pink-950 font-mono mt-0.5 block">
                  {displayTotal !== null ? `₹${Number(displayTotal).toLocaleString("en-IN")}` : "—"}
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
                <span className="text-xs text-emerald-800 font-bold block">Advance Paid</span>
                <span className="text-xl font-black text-emerald-900 font-mono mt-0.5 block">
                  {displayAdvance !== null ? `₹${Number(displayAdvance).toLocaleString("en-IN")}` : "—"}
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200">
                <span className="text-xs text-rose-800 font-bold block">Balance Due</span>
                <span className="text-xl font-black text-rose-900 font-mono mt-0.5 block">
                  {displayBalance !== null ? `₹${Number(displayBalance).toLocaleString("en-IN")}` : "—"}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {(booking.special_requirements || booking.notes) && (
            <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2">
                <FileText className="w-4 h-4 text-pink-700" />
                <span>Notes & Special Requirements</span>
              </h3>
              {booking.special_requirements && (
                <p className="text-xs text-gray-700 bg-pink-50/40 p-3 rounded-xl border border-pink-100">
                  <span className="font-bold text-pink-900">Specs: </span>
                  {booking.special_requirements}
                </p>
              )}
              {booking.notes && (
                <p className="text-xs text-gray-700 bg-pink-50/40 p-3 rounded-xl border border-pink-100">
                  <span className="font-bold text-pink-900">Notes: </span>
                  {booking.notes}
                </p>
              )}
            </div>
          )}

          {/* Status Controls */}
          <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950">
              Update Booking Status
            </h3>
            <div className="flex flex-wrap gap-2.5">
              {["confirmed", "pending", "completed", "cancelled"].map((st) => (
                <button
                  key={st}
                  disabled={isUpdatingStatus || booking.status === st}
                  onClick={() => handleUpdateStatus(st)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                    booking.status === st
                      ? "bg-pink-900 text-white shadow-sm"
                      : "bg-gray-100 text-gray-700 hover:bg-pink-100 hover:text-pink-900"
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>

          {/* Bottom Actions Bar */}
          <div className="pt-4 border-t border-pink-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <button
              onClick={handleDelete}
              className="text-xs font-bold text-rose-700 hover:text-rose-900 flex items-center gap-1.5 p-2 rounded-xl hover:bg-rose-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete / Cancel Booking</span>
            </button>

            <div className="flex items-center gap-3">
              <Link
                to={`/bookings/${id}/invoice`}
                className="px-4 py-2.5 rounded-xl border border-pink-300 text-pink-950 hover:bg-pink-50 text-xs font-bold flex items-center gap-1.5 transition-colors"
              >
                <Receipt className="w-4 h-4" />
                <span>View Full Invoice</span>
              </Link>
              <button
                onClick={() => generateInvoicePdf(booking, businessInfo)}
                className="px-5 py-2.5 rounded-xl bg-pink-900 hover:bg-pink-950 text-white text-xs font-bold shadow flex items-center gap-1.5 transition-all"
              >
                <FileDown className="w-4 h-4" />
                <span>Download PDF Invoice</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
